import React, { useState, useCallback, useEffect, useId, useRef } from "react";
import { toast } from "react-toastify";
import "../../../style/productLinkModal.css";
import ProductLinkIcon from "../../../assets/product-link.svg";
import PriceUpdateConfirmation from "./PriceUpdateConfirmation";
import { productAPI, productLinksAPI } from "../../../services";
import useDebounce from "../../../hooks/useDebounce";

/* ── SVG icon constants ───────────────────────────────────────── */
const IconPlus = ({ stroke = "#2F80ED" }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M7 1V13M1 7H13" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
    <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconUpload = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 16V8M12 8L9 11M12 8L15 11" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 16V17C4 18.6569 5.34315 20 7 20H17C18.6569 20 20 18.6569 20 17V16" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconRemove = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconTag = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const IconClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57606A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8C959F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconAddCircle = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2F80ED" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const SEARCH_DEBOUNCE_MS = 400;

/* ── Helpers ──────────────────────────────────────────────────── */
const formatPrice = (value, fallback) =>
  `€ ${parseFloat(value || fallback).toFixed(2)}`;

const createVariantRow = () => ({ id: crypto.randomUUID(), name: "", value: "" });

const getProductId = (product) => product?.id ?? product?.product_id;

const getProductSellingPrice = (product) =>
  parseFloat(product?.selling_price ?? product?.avg_selling_price ?? 0) || 0;

const buildLinkSubtitle = (item) => {
  const parts = [
    item.brand_name,
    item.category_name,
    item.unique_identifier,
  ].filter(Boolean);

  const price = item.selling_price ?? item.avg_selling_price;
  if (price != null && price !== "") {
    parts.push(`€ ${parseFloat(price).toFixed(2)}`);
  }

  const stock = item.in_stock ?? item.stock;
  if (stock != null && stock !== "") {
    parts.push(`Stock: ${stock}`);
  }

  return parts.length > 0 ? parts.join(" · ") : "—";
};

const mapSearchResult = (item) => {
  const title = (item.display_name || item.name || "").trim() || "—";
  return {
    id: item.id,
    name: title,
    modelName: item.name || "",
    brandName: item.brand_name || "",
    categoryName: item.category_name || "",
    subtitle: buildLinkSubtitle(item),
    price: parseFloat(item.selling_price ?? item.avg_selling_price ?? 0) || 0,
    stock: item.in_stock ?? item.stock ?? null,
    image: item.image || "",
  };
};

const extractApiPayload = (response) => response?.data ?? response;

const enableLinkPriceSync = async (fromProductId, toProductId) => {
  const patchLinkFlags = async (fromId, toId) => {
    const response = await productLinksAPI.updateProductLinks({
      from_product: fromId,
      links: [
        {
          product_id: toId,
          to_display: true,
          to_update_price: true,
        },
      ],
    });
    return extractApiPayload(response);
  };

  const result = await patchLinkFlags(fromProductId, toProductId);
  const updated = result?.updated ?? [];
  const reverseLink = result?.reverse_link ?? [];

  const wasUpdated = updated.some(
    (item) => String(item.to_product) === String(toProductId)
  );

  if (
    !wasUpdated &&
    reverseLink.some((id) => String(id) === String(toProductId))
  ) {
    await patchLinkFlags(toProductId, fromProductId);
  }
};

const ensureLinkedVariantsPriceSync = async (fromProductId, linkedVariants) => {
  const needsSync = linkedVariants.filter((v) => !v.to_update_price);
  if (needsSync.length === 0) return;

  await Promise.all(
    needsSync.map((v) => enableLinkPriceSync(fromProductId, v.id))
  );
};

const mapLinkedVariant = (item) => {
  const title = (item.display_name || item.name || "").trim() || "—";
  return {
    id: item.id,
    link_id: item.link_id,
    name: title,
    modelName: item.name || "",
    subtitle: buildLinkSubtitle(item),
    sku:
      item.unique_identifier ||
      item.sku ||
      item.unique_identifier_identifier ||
      "—",
    price: parseFloat(item.selling_price ?? item.avg_selling_price ?? 0) || 0,
    stock: item.in_stock ?? item.stock ?? 0,
    image: item.image || item.thumbnail || "",
    to_update_price: item.to_update_price,
    to_display: item.to_display,
  };
};

/* ── Component ────────────────────────────────────────────────── */
const ProductLinkModal = ({ isOpen, onClose, product, onPriceUpdated }) => {
  const titleId        = useId();
  const dropdownRef    = useRef(null);

  const [isNewDevice,        setIsNewDevice]        = useState(true);
  const [isUsedDevice,       setIsUsedDevice]        = useState(false);
  const [variants,           setVariants]            = useState([createVariantRow()]);
  const [showLinkDropdown,   setShowLinkDropdown]    = useState(false);
  const [linkedVariants,     setLinkedVariants]      = useState([]);
  const [showConfirm,        setShowConfirm]         = useState(false);
  const [originalSellingPrice, setOriginalSellingPrice] = useState(0);
  const [sellingPrice,       setSellingPrice]        = useState(0);
  const [isApplyingPrice,    setIsApplyingPrice]     = useState(false);
  const [searchQuery,        setSearchQuery]         = useState("");
  const [searchResults,      setSearchResults]       = useState([]);
  const [searchLoading,      setSearchLoading]       = useState(false);
  const [linksLoading,       setLinksLoading]        = useState(false);
  const [linkingProductId,   setLinkingProductId]     = useState(null);

  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const productId = getProductId(product);
  const defaultSearchTerm = (product?.name || "").trim();
  const categoryLabel = product?.category_name || "Mobile";

  const filteredResults = searchResults.filter(
    (r) => !linkedVariants.some((v) => String(v.id) === String(r.id))
  );

  const fetchLinkedVariants = useCallback(async () => {
    if (!productId) return;
    setLinksLoading(true);
    try {
      const list = await productLinksAPI.getProductLinks({
        product_id: productId,
        basic_detail: 1,
        page_size: 100,
      });
      setLinkedVariants(list.map(mapLinkedVariant));
    } catch (error) {
      console.error("Error fetching product links:", error);
      toast.error("Failed to load linked variants");
      setLinkedVariants([]);
    } finally {
      setLinksLoading(false);
    }
  }, [productId]);

  const fetchSearchResults = useCallback(async () => {
    if (!productId || !showLinkDropdown) return;

    const searchTerm = (debouncedSearch || defaultSearchTerm).trim();
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const params = {
        search: searchTerm,
        from_product: productId,
      };
      const categoryId = product?.category_id ?? product?.category;
      const brandId = product?.brand_id ?? product?.brand;
      if (categoryId != null && categoryId !== "") {
        params.category_id = categoryId;
      }
      if (brandId != null && brandId !== "") {
        params.brand_id = brandId;
      }

      const list = await productLinksAPI.searchProductsForLinking(params);
      setSearchResults(list.map(mapSearchResult));
    } catch (error) {
      console.error("Error searching products for linking:", error);
      toast.error("Failed to search variants");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [
    productId,
    showLinkDropdown,
    debouncedSearch,
    defaultSearchTerm,
    product?.category_id,
    product?.category,
    product?.brand_id,
    product?.brand,
  ]);

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showLinkDropdown) setShowLinkDropdown(false);
        else onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, showLinkDropdown]);

  /* Click-outside closes dropdown */
  useEffect(() => {
    if (!showLinkDropdown) return;
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowLinkDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showLinkDropdown]);

  /* Load existing links when modal opens */
  useEffect(() => {
    if (isOpen && productId) {
      fetchLinkedVariants();
    }
  }, [isOpen, productId, fetchLinkedVariants]);

  /* Sync selling price from product when modal opens */
  useEffect(() => {
    if (!isOpen || !product) return;
    const initialPrice = getProductSellingPrice(product);
    setOriginalSellingPrice(initialPrice);
    setSellingPrice(initialPrice);
  }, [isOpen, product]);

  /* Reset search when dropdown closes */
  useEffect(() => {
    if (!showLinkDropdown) {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [showLinkDropdown]);

  /* Debounced search when dropdown is open */
  useEffect(() => {
    if (showLinkDropdown) {
      fetchSearchResults();
    }
  }, [showLinkDropdown, fetchSearchResults]);

  const handleOverlayClick = useCallback(
    (e) => { if (e.target === e.currentTarget) onClose(); },
    [onClose]
  );

  const handleLinkVariant = useCallback(() => {
    setShowLinkDropdown((v) => !v);
  }, []);

  const handleAddLinkedVariant = useCallback(
    async (variant) => {
      if (!productId) return;

      setLinkingProductId(variant.id);
      try {
        const response = await productLinksAPI.createProductLinks({
          from_product: productId,
          links: [
            {
              product_id: variant.id,
              to_display: true,
              to_update_price: true,
            },
          ],
        });

        const payload = extractApiPayload(response);
        const skipped = payload?.skipped ?? [];
        const notFound = payload?.not_found ?? [];
        const invalid = payload?.invalid ?? [];

        if (notFound.length > 0) {
          toast.error("Product not found");
        } else if (invalid.length > 0) {
          toast.error("Cannot link product to itself");
        } else if (skipped.length > 0) {
          try {
            await enableLinkPriceSync(productId, variant.id);
            toast.info("This variant is already linked — price sync enabled");
          } catch (patchError) {
            console.error("Error enabling price sync on existing link:", patchError);
            toast.info("This variant is already linked");
          }
        } else {
          toast.success("Variant linked successfully");
        }

        await fetchLinkedVariants();
        setSearchResults((prev) =>
          prev.filter((r) => String(r.id) !== String(variant.id))
        );
      } catch (error) {
        console.error("Error creating product link:", error);
        const message =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.message ||
          "Failed to link variant";
        toast.error(message);
      } finally {
        setLinkingProductId(null);
      }
    },
    [productId, fetchLinkedVariants]
  );

  const handleUnlinkVariant = useCallback(
    async (variant) => {
      const linkId = variant.link_id;
      if (!linkId) {
        setLinkedVariants((prev) =>
          prev.filter((v) => String(v.id) !== String(variant.id))
        );
        return;
      }

      try {
        await productLinksAPI.deleteProductLink(linkId);
        toast.success("Variant unlinked");
        setLinkedVariants((prev) =>
          prev.filter((v) => String(v.link_id) !== String(linkId))
        );
      } catch (error) {
        console.error("Error deleting product link:", error);
        toast.error("Failed to unlink variant");
      }
    },
    []
  );

  const handleAddVariant = useCallback(() => {
    setVariants((prev) => [...prev, createVariantRow()]);
  }, []);

  const handleRemoveVariant = useCallback((id) => {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const handleApplySellingPriceUpdate = useCallback(async () => {
    if (!productId) {
      toast.error("Product not found");
      return;
    }

    const nextPrice = parseFloat(sellingPrice);
    if (Number.isNaN(nextPrice) || nextPrice < 0) {
      toast.error("Please enter a valid selling price");
      return;
    }

    setIsApplyingPrice(true);
    try {
      if (linkedVariants.length > 0) {
        await ensureLinkedVariantsPriceSync(productId, linkedVariants);
      }

      await productAPI.updateProductSellingPrice({
        product_id: productId,
        selling_price: nextPrice,
        update_price_for_product:
          linkedVariants.length > 0 ? "__all__" : undefined,
      });
      toast.success("Selling price updated successfully");
      setShowConfirm(false);
      onPriceUpdated?.();
      onClose();
    } catch (error) {
      console.error("Error updating selling price:", error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        (Array.isArray(error.response?.data?.non_field_errors)
          ? error.response.data.non_field_errors[0]
          : null) ||
        error.message ||
        "Failed to update selling price";
      toast.error(message);
      throw error;
    } finally {
      setIsApplyingPrice(false);
    }
  }, [productId, sellingPrice, linkedVariants, onClose, onPriceUpdated]);

  const handleUpdateClick = () => {
    const nextPrice = parseFloat(sellingPrice);
    if (Number.isNaN(nextPrice) || nextPrice < 0) {
      toast.error("Please enter a valid selling price");
      return;
    }

    if (linkedVariants.length > 0) {
      setShowConfirm(true);
      return;
    }

    handleApplySellingPriceUpdate();
  };

  const handleVariantChange = useCallback((id, field, value) => {
    setVariants((prev) =>
      prev.map((v) => (v.id === id ? { ...v, [field]: value } : v))
    );
  }, []);

  if (!isOpen || !product) return null;

  return (
    <div
      className="plm-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="plm-container">

        {/* Header */}
        <div className="plm-header">
          <h2 id={titleId} className="plm-title">Edit Product</h2>
          <p className="plm-subtitle">Only product names can be edited after creation</p>
        </div>
        <div className="plm-divider" />

        {/* Body */}
        <div className="plm-body">

          {/* Category / Brand + Thumbnail */}
          <div className="plm-top-grid">
            <div className="plm-left-fields">
              <div className="plm-field-col">
                <label className="plm-label">Category *</label>
                <div className="plm-input-readonly">
                  <span className="plm-input-text">{product.category_name || "Mobile"}</span>
                </div>
              </div>
              <div className="plm-field-col">
                <label className="plm-label">Brand Name *</label>
                <div className="plm-input-readonly">
                  <span className="plm-input-text">{product.brand_name || "Apple"}</span>
                </div>
              </div>
            </div>

            <div className="plm-field-col plm-thumbnail-col">
              <label className="plm-label">Thumbnail Image</label>
              <div className={`plm-image-box${!product.image ? " plm-image-box-empty" : ""}`}>
                {product.image ? (
                  <img src={product.image} alt={product.name} className="plm-thumb" />
                ) : (
                  <div className="plm-thumb-placeholder">
                    <div className="plm-upload-content">
                      <IconUpload />
                      <span className="plm-upload-text">The image should be JPG, PNG, WEBP format</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Model Name + Suffix */}
          <div className="plm-row">
            <div className="plm-field-col">
              <label className="plm-label">Model Name *</label>
              <div className="plm-input-readonly">
                <span className="plm-input-text">{product.name || "iPhone 16 Pro"}</span>
              </div>
            </div>
            <div className="plm-field-col">
              <label className="plm-label">Suffix</label>
              <div className="plm-input-editable">
                <input
                  type="text"
                  className="plm-input"
                  placeholder="e.g. Pro Max"
                  defaultValue={product.suffix || ""}
                />
              </div>
            </div>
          </div>

          {/* RAM + Storage */}
          <div className="plm-row">
            <div className="plm-field-col">
              <label className="plm-label">RAM</label>
              <div className="plm-input-readonly">
                <span className="plm-input-text">{product.ram || "8GB"}</span>
              </div>
            </div>
            <div className="plm-field-col">
              <label className="plm-label">Storage</label>
              <div className="plm-input-readonly">
                <span className="plm-input-text">{product.storage || "256GB"}</span>
              </div>
            </div>
          </div>

          {/* Device type checkboxes */}
          <div className="plm-row plm-row-checkbox">
            <div
              className="plm-checkbox-group"
              role="checkbox"
              aria-checked={isNewDevice}
              tabIndex={0}
              onClick={() => setIsNewDevice((v) => !v)}
              onKeyDown={(e) => e.key === " " && setIsNewDevice((v) => !v)}
            >
              <div className={isNewDevice ? "plm-checkbox-checked" : "plm-checkbox-empty"}>
                {isNewDevice && <IconCheck />}
              </div>
              <span className="plm-checkbox-label">New Device</span>
            </div>

            <div
              className="plm-checkbox-group"
              role="checkbox"
              aria-checked={isUsedDevice}
              tabIndex={0}
              onClick={() => setIsUsedDevice((v) => !v)}
              onKeyDown={(e) => e.key === " " && setIsUsedDevice((v) => !v)}
            >
              <div className={isUsedDevice ? "plm-checkbox-checked" : "plm-checkbox-empty"}>
                {isUsedDevice && <IconCheck />}
              </div>
              <span className="plm-checkbox-label">Used Device</span>
            </div>
          </div>

          {/* Pricing */}
          <div className="plm-section-box">
            <label className="plm-section-label">Pricing</label>
            <div className="plm-pricing-row">
              <div className="plm-pricing-col">
                <span className="plm-pricing-sublabel">Avg. Buying Price</span>
                <div className="plm-input-disabled">
                  <span className="plm-input-text">{formatPrice(product.avg_buying_price, 499)}</span>
                </div>
              </div>
              <div className="plm-pricing-col">
                <span className="plm-pricing-sublabel">Selling Price *</span>
                <div className="plm-input-editable">
                  <input
                    type="number"
                    className="plm-input"
                    value={sellingPrice}
                    onChange={(e) => setSellingPrice(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Linked Variants */}
          <div className="plm-section-box plm-linked-variants-box" ref={dropdownRef}>
            <div className="plm-linked-header">
              <div className="plm-linked-icon-wrap">
                <img src={ProductLinkIcon} alt="" className="plm-linked-icon" />
              </div>
              <div className="plm-linked-header-text">
                <span className="plm-linked-title">Linked Variants</span>
                <p className="plm-linked-subtitle">
                  Same product, different variant - prices stay in sync · Mobile only
                </p>
              </div>
              {linkedVariants.length > 0 && (
                <div className="plm-linked-badge">{linkedVariants.length} Linked</div>
              )}
            </div>

            {/* Linked Cards */}
            {linksLoading && linkedVariants.length === 0 && (
              <div className="plm-ld-empty">Loading linked variants...</div>
            )}
            {linkedVariants.length > 0 && (
              <div className="plm-linked-cards">
                {linkedVariants.map((v) => (
                  <div key={v.link_id || v.id} className="plm-linked-card">
                    <div className="plm-linked-card-img">
                      {v.image ? (
                        <img src={v.image} alt="" />
                      ) : (
                        <div className="plm-linked-card-img-placeholder" />
                      )}
                    </div>
                    <div className="plm-linked-card-info">
                      <span className="plm-linked-card-name">{v.name}</span>
                      <span className="plm-linked-card-sku">{v.subtitle}</span>
                    </div>
                    <div className="plm-linked-card-price">€ {v.price.toFixed(2)}</div>
                    <button
                      type="button"
                      className="plm-unlink-btn"
                      onClick={() => handleUnlinkVariant(v)}
                      disabled={linksLoading}
                    >
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search Dropdown */}
            {showLinkDropdown && (
              <div className="plm-link-dropdown">
                <div className="plm-ld-top-bar">
                  <IconTag />
                  <span className="plm-ld-top-text">
                    Showing <span className="plm-ld-top-underline">{categoryLabel}</span> variants only
                  </span>
                  <button
                    type="button"
                    className="plm-ld-top-close"
                    onClick={() => setShowLinkDropdown(false)}
                    aria-label="Close dropdown"
                  >
                    <IconClose />
                  </button>
                </div>
                <div className="plm-ld-search-wrap">
                  <IconSearch />
                  <input
                    type="text"
                    className="plm-ld-search-input"
                    placeholder="Search by name, color, or SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="plm-ld-list">
                  {searchLoading ? (
                    <div className="plm-ld-empty">Searching...</div>
                  ) : filteredResults.length === 0 ? (
                    <div className="plm-ld-empty">No results found</div>
                  ) : (
                    filteredResults.map((res) => (
                      <div key={res.id} className="plm-ld-item">
                        <div className="plm-ld-img-box">
                          {res.image ? (
                            <img src={res.image} alt="" className="plm-ld-img" />
                          ) : (
                            <div className="plm-ld-img plm-ld-img-placeholder" />
                          )}
                        </div>
                        <div className="plm-ld-info">
                          <span className="plm-ld-name">{res.name}</span>
                          <span className="plm-ld-sku">{res.subtitle}</span>
                        </div>
                        <button
                          type="button"
                          className="plm-ld-add-btn"
                          onClick={() => handleAddLinkedVariant(res)}
                          disabled={linkingProductId === res.id}
                          aria-label={`Link ${res.name}`}
                        >
                          <IconAddCircle />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <button type="button" className="plm-link-variant-btn" onClick={handleLinkVariant}>
              <IconPlus />
              <span>Link a variant to this product</span>
            </button>

            {linkedVariants.length > 0 && (
              <p className="plm-sync-msg">
                Selling price changes will prompt sync across {linkedVariants.length + 1} variants
              </p>
            )}
          </div>

          {/* Variant Features */}
          <div className="plm-section-box">
            <div className="plm-variant-header">
              <label className="plm-section-label">Variant Features</label>
              <button type="button" className="plm-add-variant-btn" onClick={handleAddVariant}>
                <IconPlus stroke="#424A53" />
                <span>Add Variant</span>
              </button>
            </div>

            <div className="plm-variant-list">
              {variants.map((variant) => (
                <div key={variant.id} className="plm-variant-row">
                  <div className="plm-input-editable">
                    <input
                      type="text"
                      className="plm-input"
                      placeholder="Feature name (e.g. Color)"
                      value={variant.name}
                      onChange={(e) => handleVariantChange(variant.id, "name", e.target.value)}
                    />
                  </div>
                  <div className="plm-input-editable">
                    <input
                      type="text"
                      className="plm-input"
                      placeholder="Feature value (e.g. Black)"
                      value={variant.value}
                      onChange={(e) => handleVariantChange(variant.id, "value", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="plm-footer">
          <button type="button" className="plm-cancel-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="plm-update-btn" onClick={handleUpdateClick}>Update Product</button>
        </div>

        <PriceUpdateConfirmation
          isOpen={showConfirm}
          onCancel={() => !isApplyingPrice && setShowConfirm(false)}
          onApply={handleApplySellingPriceUpdate}
          currentPrice={originalSellingPrice}
          newPrice={sellingPrice}
          linkedVariants={linkedVariants}
          isSubmitting={isApplyingPrice}
        />

      </div>
    </div>
  );
};

export default ProductLinkModal;
